import { authApiInstance } from './api'

export const authService = {
  login: (credentials) => authApiInstance.post('/auth/login', credentials),
  refreshToken: (tokenData) => authApiInstance.post('/auth/refresh-token', tokenData),
  forgotPassword: (email) => authApiInstance.post('/auth/forgot-password', { email }),
  resetPassword: (resetData) => authApiInstance.post('/auth/reset-password', resetData),
  externalLogin: (data) => authApiInstance.post('/auth/external-login', data),
}

// Token management
export const tokenManager = {
  getToken: () => localStorage.getItem('authToken'),
  getRefreshToken: () => localStorage.getItem('refreshToken'),
  setTokens: (token, refreshToken) => {
    localStorage.setItem('authToken', token)
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken)
    }
  },
  clearTokens: () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('userProfile')
  },
  getUserProfile: () => {
    const profile = localStorage.getItem('userProfile')
    return profile ? JSON.parse(profile) : null
  },
  setUserProfile: (profile) => {
    localStorage.setItem('userProfile', JSON.stringify(profile))
  }
}

// A single in-flight refresh, shared by everything that needs one.
//
// The server rotates the refresh token on every use and refuses one it has already
// replaced (AuthService.RefreshTokenAsync), so simultaneous refreshes all spending the
// same token leave every call after the first refused — and each of those signed the
// session out, throwing away the credentials the winner had just stored. One expiry has
// to spend exactly one refresh token, however many requests run into it at once.
let inFlightRefresh = null

const performRefresh = async () => {
  const currentRefreshToken = tokenManager.getRefreshToken()
  if (!currentRefreshToken) return null

  const response = await authService.refreshToken({
    token: tokenManager.getToken(),
    refreshToken: currentRefreshToken,
  })

  const { token, refreshToken } = response.data
  tokenManager.setTokens(token, refreshToken)
  return token
}

/**
 * Renew the access token, joining a refresh that is already running rather than starting
 * a second one.
 *
 * Resolves with the new access token, or null when there is no refresh token to spend.
 * Rejects when a refresh was actually attempted and refused.
 */
export const refreshAccessToken = () => {
  if (!inFlightRefresh) {
    // Assigned before anything awaits it, so a caller arriving later in the same tick
    // finds it already set and joins instead of starting its own. Cleared once it
    // settles, so a genuinely later expiry starts a fresh refresh rather than replaying
    // this result.
    inFlightRefresh = performRefresh().finally(() => {
      inFlightRefresh = null
    })
  }
  return inFlightRefresh
}
